/**
 * 站点自动分类模块
 * 根据 URL/域名自动识别站点类型，用于"自动分类整理"
 */

import type { PasswordCategory } from "./types";

/** 分类关键词规则表 */
const CATEGORY_RULES: { category: PasswordCategory; keywords: string[] }[] = [
  {
    category: "social",
    keywords: [
      "weibo", "qq", "wechat", "weixin", "douyin", "tiktok", "bilibili",
      "zhihu", "twitter", "x.com", "facebook", "instagram", "linkedin",
      "xiaohongshu", "xiaohongshu", "renren", "tieba", "reddit", "telegram",
      "discord", "snapchat", "kuaishou", "douban",
    ],
  },
  {
    category: "email",
    keywords: [
      "mail", "gmail", "outlook", "hotmail", "163.yun", "protonmail",
      "yahoo", "foxmail", "icloud", "yandex",
    ],
  },
  {
    category: "shopping",
    keywords: [
      "taobao", "tmall", "jd.com", "jingdong", "pinduoduo", "1688",
      "amazon", "ebay", "aliexpress", "dangdang", "suning", "vip.com",
    ],
  },
  {
    category: "finance",
    keywords: [
      "bank", "alipay", "unionpay", "icbc", "ccb", "abc", "boc", "citic",
      "paypal", "stripe",
    ],
  },
  {
    category: "work",
    keywords: [
      "zoom", "teams", "slack", "feishu", "lark", "dingtalk", "wps",
      "office", "confluence", "jira", "trello", "notion",
    ],
  },
  {
    category: "entertainment",
    keywords: [
      "youtube", "netflix", "spotify", "iqiyi", "youku", "tencent",
      "douyu", "huya", "steam", "epicgames", "riot", "blizzard", "wegame",
    ],
  },
  {
    category: "developer",
    keywords: [
      "github", "gitlab", "stackoverflow", "npm", "docker", "aws", "azure",
      "cloudflare", "vercel", "netlify", "digitalocean", "gitee", "coding",
      "juejin", "csdn", "leetcode",
    ],
  },
  {
    category: "ai",
    keywords: [
      "openai", "chatgpt", "claude", "gemini", "copilot", "midjourney",
      "huggingface", "anthropic", "bard", "mistral",
      "sora", "groq", "perplexity", "deepseek", "kimi",
    ],
  },
];

/** 从 URL 提取规范化域名 */
export function extractDomain(url: string): string {
  try {
    const u = new URL(url.includes("://") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return url
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .replace(/\/.*$/, "")
      .toLowerCase()
      .trim();
  }
}

/** 根据 URL/域名自动分类 */
export function classifyUrl(url: string): PasswordCategory {
  const domain = extractDomain(url);
  const haystack = `${domain} ${url}`.toLowerCase();
  for (const rule of CATEGORY_RULES) {
    for (const kw of rule.keywords) {
      if (haystack.includes(kw.toLowerCase())) {
        return rule.category;
      }
    }
  }
  return "other";
}

/** 从 URL 生成站点图标地址 (Google favicon 服务) */
export function getFavicon(url: string): string {
  const domain = extractDomain(url);
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : "";
}

/** 根据域名生成一个友好的标题 */
export function makeTitleFromUrl(url: string): string {
  const domain = extractDomain(url);
  if (!domain) return "未命名站点";
  const parts = domain.split(".");
  const main = parts.length > 1 ? parts[parts.length - 2] : parts[0];
  return main.charAt(0).toUpperCase() + main.slice(1);
}

/** 分类的中文名称映射 */
export const CATEGORY_NAMES: Record<PasswordCategory, string> = {
  social: "社交",
  email: "邮箱",
  shopping: "购物",
  finance: "金融",
  work: "办公",
  entertainment: "娱乐",
  developer: "开发",
  ai: "AI",
  other: "其他",
};